/**
 * TVH Scroll Animations (lighter version)
 * Gentle fade + slide-up as sections scroll into view.
 *
 * What changed from the old version:
 *  - Only animates your TVH elements and top-level theme sections
 *    (the old one matched every <section>, footer, and "header ~ *").
 *  - Reads all positions first, then writes classes once (avoids forced reflow).
 *  - Starts after the page has painted, so it never competes with loading.
 *  - Triggers as soon as ANY part of an element is visible, so very tall
 *    sections (like long product grids on phones) can never stay hidden.
 *  - Caps the stagger delay and drops will-change (less memory on phones).
 *  - Skips everything for visitors who prefer reduced motion.
 *  - Injects its CSS only once.
 */
(() => {
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!('IntersectionObserver' in window)) return;

  const DURATION   = 500;  // ms
  const DELAY_STEP = 70;   // ms between siblings
  const MAX_STAGGER = 4;   // delay never exceeds MAX_STAGGER * DELAY_STEP
  const OFFSET     = 24;   // px the element starts below its final spot

  const SELECTORS = [
    '.tvh-hero', '.tvh-kicker', '.tvh-heading', '.tvh-card', '.tvh-step',
    '.tvh-feature', '.tvh-reward', '.tvh-reward-item', '.tvh-faq details',
    '.tvh-cta', '.tvh-soft-card', '.tvh-product', '.tvh-rate-list',
    '.tvh-not-accepted',
    '#MainContent > .shopify-section',
    'footer .shopify-section'
  ].join(', ');

  const injectCSS = () => {
    if (document.getElementById('tvh-scroll-anim-styles')) return;
    const style = document.createElement('style');
    style.id = 'tvh-scroll-anim-styles';
    style.textContent = `
      .tvh-anim-ready {
        opacity: 0;
        transform: translateY(${OFFSET}px);
        transition: opacity ${DURATION}ms ease-out, transform ${DURATION}ms ease-out;
      }
      .tvh-anim-visible { opacity: 1; transform: none; }
    `;
    document.head.appendChild(style);
  };

  let observer;
  const getObserver = () => {
    if (observer) return observer;
    observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const el = entry.target;
        el.classList.add('tvh-anim-visible');
        observer.unobserve(el);
        // Clear the delay afterwards so hover effects stay snappy.
        setTimeout(() => { el.style.transitionDelay = ''; }, DURATION + 400);
      }
    }, { threshold: 0, rootMargin: '0px 0px -8% 0px' });
    return observer;
  };

  const init = () => {
    injectCSS();
    const io = getObserver();
    const vh = window.innerHeight;

    // Pass 1: READ everything (no writes, so the browser lays out only once).
    const candidates = [];
    document.querySelectorAll(SELECTORS).forEach((el) => {
      if (el.dataset.tvhAnim) return;                       // already handled
      if (el.closest('#header-group')) return;              // never animate the header
      if (el.parentElement && el.parentElement.closest('.tvh-anim-ready')) return;
      const r = el.getBoundingClientRect();
      if (r.width < 40 || r.height < 20) return;
      candidates.push({ el, belowFold: r.top >= vh });
    });

    // Pass 2: WRITE.
    const countBySibling = new Map();
    for (const { el, belowFold } of candidates) {
      el.dataset.tvhAnim = '1';
      if (!belowFold) continue;                             // visible on load: leave alone
      const parent = el.parentElement;
      const n = countBySibling.get(parent) || 0;
      countBySibling.set(parent, n + 1);
      el.style.transitionDelay = `${Math.min(n, MAX_STAGGER) * DELAY_STEP}ms`;
      el.classList.add('tvh-anim-ready');
      io.observe(el);
    }
  };

  const start = () => requestAnimationFrame(() => setTimeout(init, 0));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // Theme editor: animate newly loaded sections.
  document.addEventListener('shopify:section:load', () => setTimeout(init, 100));
})();